import React, {createRef, useLayoutEffect, useMemo, useState} from 'react';
import {Stack} from '@mui/material';
import type {ThesaurusAction} from 'types';
import type {
  MultiThesaurusFieldConfig,
  MultiThesaurusValue,
} from 'sceneExtensions/extensions/multiThesaurusEdit/types';
import type {SetSourceValue} from 'sceneExtensions/types';
import {useLocalization} from 'localization';
import {HeaderHeight} from 'theme/settings';
import {FlexBox, SearchInput, Spacer} from 'components';
import {SceneTitle} from 'components/scene/SceneTitle';
import {ThesaurusToolbar} from '../editThesaurus/components/ThesaurusToolbar';
import {SearchResultContainer} from './components';
import {
  MultiThesaurusEditStateContext,
  useMultiThesaurusEditStateContextProviderValue,
} from './contexts';

type Props = {
  fieldConfig?: MultiThesaurusFieldConfig;
  thesaurusValue?: MultiThesaurusValue;
  onSetSourceValue: SetSourceValue<MultiThesaurusValue, ThesaurusAction>;
  onClose: () => void;
};

export const MultiThesaurusEdit: React.FC<Props> = ({
  fieldConfig,
  thesaurusValue,
  onSetSourceValue,
  onClose,
}) => {
  const {t} = useLocalization();

  const thesaurusEditState = useMultiThesaurusEditStateContextProviderValue(
    fieldConfig?.thesauruses,
    thesaurusValue ?? {},
    onSetSourceValue,
    onClose,
  );
  const {thesaurusIds, searchQuery, setSearchQuery} = thesaurusEditState;
  const {placeholder, title} = useMemo(() => {
    const labels = thesaurusIds.map(id => t(`thesaurus.${id}.root.label`));
    return {
      placeholder: t('search.multiThesaurus.placeholder'),
      title:
        labels.length > 1
          ? t('search.multiThesaurus.titleList', {
              thesaurusList: labels.slice(0, labels.length - 1).join(', '),
              lastThesaurus: labels[labels.length - 1],
            })
          : 'Missing',
    };
  }, [t, thesaurusIds]);

  /**
   * LayoutHeaderHeight is used to inform the below Scroll-component of the offset.
   * We initially just guess 200, but useEffect will update this to the correct amount.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [layoutHeaderHeight, setLayoutHeaderHeight] = useState(200);
  const layoutHeaderRef = createRef<HTMLDivElement>();
  useLayoutEffect(() => {
    if (layoutHeaderRef && layoutHeaderRef.current) {
      setLayoutHeaderHeight(
        layoutHeaderRef.current.clientHeight + HeaderHeight * 2,
      );
    }
  }, [layoutHeaderRef]);

  return (
    <MultiThesaurusEditStateContext.Provider value={thesaurusEditState}>
      {/* Header with search input */}
      <FlexBox ref={layoutHeaderRef}>
        <SceneTitle title={title}>
          <ThesaurusToolbar onClose={onClose} />
        </SceneTitle>

        <Spacer size={1} />

        <SearchInput
          value={searchQuery ?? ''}
          placeholder={placeholder}
          triggerFocus={fieldConfig}
          onChange={setSearchQuery}
        />
        <Spacer size={3} />
        <Stack spacing={4}>
          {/* Search result pr thesaurus */}
          {searchQuery
            ? thesaurusIds.map(id => (
                <SearchResultContainer key={id} thesaurusId={id} />
              ))
            : null}
        </Stack>
      </FlexBox>
    </MultiThesaurusEditStateContext.Provider>
  );
};
